import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { isMobile } from 'react-device-detect';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { selectSong } from 'services/store/newSongs/selectors';
import useGetSongGenre from 'services/hooks/songs/useGetSongGenre';
import aiMusicGenerator from 'services/locales/en/aiMusicGenerator';
import { locations } from 'services/routes/locations';
import { multiplesOf } from 'services/store/helpers/getMultiple';
import type {
  AppliedFiltersType,
  SelectedTagsType,
  TagStore,
} from 'services/store/tags/types';
import theme from 'theme';
import SongWaveform from 'components/SongWaveform';
import { AudioPlayerButton } from 'components/PlayerButton';
import ViewportMarker from 'components/ViewportMarker';
import { analytics, events } from 'services/analytics';
import { AnalyticsEventLocation } from 'services/analytics/entities';
import { SongType } from 'services/api/entities';
import { toColonTimeFromMs } from 'services/helpers/time';
import SongActionsContainer from 'components/SongActionButtons/';
import { SongListItemVariant } from './SongListItemTypes';
import SongTagsChips from './components/SongTagsChips';
import SongMoodItems from './components/SongMoodsItems';
import useStyles, {
  Container,
  DurationContainer,
  TagsContainer,
  WaveformContainer,
  SongActionsContainer as StyledSongActionsContainer,
  StyledListItem,
  PlayerContainer,
  SeekbarContainer,
} from './styled';
import MicSection from './components/MicSection';

type PropsType = {
  songId: string;
  index: number;
  variant?: SongListItemVariant;
  page?: number;
  tagStore: TagStore;
  isFiltered?: number;
  songIdsLength: number;
  getSongs(params: {
    page: number;
    selectedTags?: SelectedTagsType;
    appliedFilters?: AppliedFiltersType;
  }): void;
};

const SongListItem = (props: PropsType) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const {
    songId,
    variant = 'default',
    page,
    tagStore,
    songIdsLength,
    isFiltered,
    index,
    getSongs,
  } = props;

  const isOnSongsLibraryPage = variant === 'library';
  const paginationMultiples = multiplesOf(songIdsLength);
  const [withWaypoint, setWithWaypoint] = useState(false);

  const song = useSelector(selectSong(songId));
  const indexFetchSongs = 7;

  const { genre } = useGetSongGenre(song);

  useEffect(() => {
    if (isFiltered && index === songIdsLength - indexFetchSongs) {
      setWithWaypoint(true);
    }
    if (!paginationMultiples) {
      setWithWaypoint(true);
    } else {
      setWithWaypoint(false);
    }
  }, [index, songIdsLength, paginationMultiples, isFiltered]);

  const requestMoreData = () => {
    const params = {
      // @ts-ignore
      page: page + 1,
      selectedTags: tagStore?.selectedTags,
      appliedFilters: tagStore?.appliedFilters,
    };
    getSongs(params);
  };

  const getLocation = () => {
    if (isOnSongsLibraryPage) {
      return AnalyticsEventLocation.LIBRARY;
    }

    return AnalyticsEventLocation.MAIN_LIST;
  };

  const handleTrackDetail = () => {
    if (!song) {
      return;
    }
    analytics.track(events.trackDetail(song.title, genre, getLocation()));
  };

  const renderWaypointIfNeeded = () => {
    if (withWaypoint) {
      return (
        <ViewportMarker
          // @ts-ignore
          indexFetchSongs={indexFetchSongs}
          length={songIdsLength}
          index={index}
          onEnterViewport={() => requestMoreData()}
        />
      );
    }
    return null;
  };

  const displayDuration = () => (
    <DurationContainer data-testid="songDuration">
      <Typography
        variant="body1"
        sx={{
          fontSize: isMobile ? 12 : 16,
        }}
      >
        {toColonTimeFromMs(song?.duration)}
      </Typography>
    </DurationContainer>
  );

  const tagsSection = () => {
    if (!song || !genre) {
      return;
    }
    return <SongTagsChips song={song} />;
  };

  const displayTitle = (title: string) => {
    if (!song) {
      return null;
    }
    const { songDetails } = locations.general;
    const { id, songType } = song;
    return (
      // @ts-ignore
      <Typography
        onClick={handleTrackDetail}
        variant="body2"
        color="inherit"
        component={Link}
        to={{
          pathname: songDetails.pathname(
            id,
            title,
            songType === SongType.AI_GENERATED
          ),
        }}
        data-testid="song-list-item-song-title"
        className={classes.title}
        title={title}
        sx={{ [theme.breakpoints.down('md')]: { fontSize: 14 } }}
      >
        {title}
      </Typography>
    );
  };

  const displayTitleSection = () => {
    if (!song) {
      return;
    }
    return (
      <div className={classes.titleContainer}>
        {displayTitle(song.title)}
        {isMobile && displayDuration()}
      </div>
    );
  };

  const displayMoodsSection = () =>
    song?.songType === SongType.AI_GENERATED ? (
      <Box
        sx={{
          color: theme.palette.secondary.main,
        }}
      >
        {t(aiMusicGenerator.tag.tagName)}
      </Box>
    ) : (
      <SongMoodItems song={song} />
    );

  const displayWaveformSection = () => {
    if (!song) {
      return null;
    }

    return (
      <>
        {!isMobile && displayDuration()}

        <WaveformContainer>
          <SongWaveform song={song} />
        </WaveformContainer>

        <StyledSongActionsContainer>
          <SongActionsContainer song={song} location={getLocation()} />
        </StyledSongActionsContainer>
      </>
    );
  };

  return (
    <>
      <StyledListItem key={song?.id} sx={{}}>
        {renderWaypointIfNeeded()}
        <Container>
          <PlayerContainer>
            <Box padding="4px">
              <AudioPlayerButton
                song={song}
                location={
                  isOnSongsLibraryPage
                    ? AnalyticsEventLocation.LIBRARY
                    : AnalyticsEventLocation.MAIN_LIST
                }
              />
            </Box>
            {displayTitleSection()}
            <MicSection song={song} />
          </PlayerContainer>
          <TagsContainer>
            {tagsSection()}
            {displayMoodsSection()}
          </TagsContainer>

          <SeekbarContainer>{displayWaveformSection()}</SeekbarContainer>
        </Container>
      </StyledListItem>
    </>
  );
};

export default SongListItem;
